import { PluginInterface } from '../../types/PluginInterface';
import { generateUniformPlaceholder } from '../../utils/PlaceholderGenerator';
import { EcommerceConfig } from './types';
import { defaultConfig } from './config';
import { EcommerceConfigComponent } from './EcommerceConfigComponent';
import { EcommerceShopRenderer } from './EcommerceShopRenderer';

const generateEcommerceHTML = (config: EcommerceConfig): string => {
  // 1. Génération ID unique (si absent ou égal à la valeur par défaut)
  const isDefaultId = !config.id || config.id === 'ecomm-shop-modular';
  const instanceId = isDefaultId 
    ? `shop-${Date.now()}-${Math.floor(Math.random() * 1000)}` 
    : config.id!;

  // 2. Persistance de l'ID
  const configWithId = { ...config, id: instanceId };

  const title = config.title || config.shopName || 'Boutique';
  const description = config.description || config.shopDescription || `Boutique avec ${config.products?.length || 0} produits.`;

  return generateUniformPlaceholder({
    pluginId: instanceId, // ✅ Utilisation de l'ID unique pour le ciblage
    pluginType: 'ecommerce-shop',
    category: 'commerce',
    title: title,
    description: description,
    icon: '🛍️',
    config: configWithId // ✅ On passe la config enrichie avec l'ID
  });
};

const EcommerceShopPlugin: PluginInterface = {
  metadata: {
    id: 'ecommerce-shop',
    name: 'Boutique E-commerce',
    description: 'Crée une boutique en ligne complète avec gestion de produits, panier et paiement.',
    icon: '🛍️',
    category: 'commerce',
    version: '1.2.0',
    author: 'Book-Editor Team',
    tags: ['ecommerce', 'shop', 'payment', 'store', 'cart'],
  },
  
  // ✅ DOCUMENTATION EXHAUSTIVE POUR L'ASSISTANT IA (ContainerWizard)
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Boutique en ligne complète (produits, panier, paiements, livraison/Click&Collect, emails, CGU/RGPD).",
    usage: "Utilise ce plugin lorsque l'utilisateur veut vendre des produits, créer un catalogue, ou une page de paiement avec pages légales.",
    
    // Définition structurée des capacités
    capabilities: {
      products: "Gestion liste produits (Nom, Prix, Description, Images, Catégorie, Stock, Image principale).",
      payment: "Stripe, PayPal, Paiement à la livraison (Cash).",
      delivery: "Expédition standard (frais globaux ou par produit) ou Click & Collect.",
      tax: "Gestion TVA globale ou par produit.",
      styling: "Personnalisation complète : Couleurs (Fond, Header, Footer, Produits), Typographie (Titres, Prix, Descriptions), Transparence.",
      emails: "Templates d'emails personnalisables pour acheteur et vendeur.",
      legal: "CGU/CGV + RGPD via TinyMCE, liens en footer."
    },

    // Schéma de configuration mapé sur EcommerceConfig
    configSchema: {
      type: "object",
      properties: {
        shopName: { type: "string", description: "Nom de la boutique affiché en haut." },
        shopDescription: { type: "string", description: "Slogan ou description courte sous le titre." },
        currency: { type: "string", description: "Devise (EUR, USD, GBP, etc.)." },
        
        // Structure Produit
        products: {
          type: "array",
          items: {
            type: "object",
            properties: {
              name: "string",
              price: "number",
              description: "string",
              image: "string (URL)",
              images: "array of strings (URLs)",
              category: "string",
              stock: "number",
              shippingCost: "number (optionnel)",
              rating: "number (1-5)"
            }
          }
        },

        // Apparence & Zones
        backgroundColor: { type: "string", description: "Couleur de fond globale ou 'transparent'." },
        headerBackgroundColor: { type: "string", description: "Couleur de fond de la zone de recherche/panier." },
        productBackgroundColor: { type: "string", description: "Couleur de fond de la carte produit." },
        footerBackgroundColor: { type: "string", description: "Couleur de fond du pied de page (stats)." },
        textColor: { type: "string", description: "Couleur principale du texte." },
        
        // Typographie Titre
        titleColor: { type: "string" },
        titleSize: { type: "string", enum: ["1.5rem", "2rem", "3rem", "4rem"] },
        titleBold: { type: "boolean" },
        titleItalic: { type: "boolean" },
        
        // Typographie Description Boutique
        shopDescriptionColor: { type: "string" },
        shopDescriptionSize: { type: "string" },
        shopDescriptionBold: { type: "boolean" },

        // Style Prix
        priceColor: { type: "string" },
        priceSize: { type: "string", enum: ["1rem", "1.5rem", "2rem", "3rem"] },
        priceFontFamily: { type: "string" },
        priceBold: { type: "boolean" },
        
        // Style Boutons
        buttonColor: { type: "string", description: "Couleur de fond des boutons (Ajouter, Panier)." },
        buttonTextColor: { type: "string", description: "Couleur du texte des boutons." },

        // Paramètres
        paymentMethods: {
          stripe: "boolean",
          paypal: "boolean",
          cash: "boolean"
        },
        clickAndCollect: {
          enabled: "boolean",
          address: "string (Adresse du magasin)",
          instructions: "string"
        },
        globalShippingCost: { type: "number" },
        globalTaxRate: { type: "number" },
        termsAndConditions: { type: "string", description: "CGU/CGV en HTML." },
        termsRgpd: { type: "string", description: "RGPD en HTML." }
      }
    },

    // ── Description visuelle complète de l'interface (pour génération de plugins similaires) ──
    uiStructure: `
LAYOUT GLOBAL :
  Header (fixe) → Zone produits (scrollable) → Panier (drawer droit) → Modals (portal)

HEADER :
  - Logo boutique (image) + Titre (h1) + Description courte
  - Barre de recherche (filtre en temps réel sur nom + description)
  - Dropdown catégorie (Toutes + liste dynamique)
  - Bouton panier : icône 🛒 + badge rond orange (compteur articles)

CONTRÔLES AU-DESSUS DE LA GRILLE :
  - Bascule vue : [⊞ Grid] [☰ List] [◁▷ Carousel] — icônes Bootstrap bi-grid/bi-list/bi-chevrons
  - Sélecteur tri : Prix ↑ / Prix ↓ / Meilleures notes / Nom A→Z

CARTE PRODUIT (Grid — colonnes responsives col-12 col-sm-6 col-md-4) :
  - Image (height: 220px, objectFit: 'cover', width: '100%')
  - Overlays sur l'image :
      • Badge pastille haut-gauche : "NEW" (bleu) | "SALE" (rouge) | "HOT" (orange)
      • Bouton cœur haut-droit : ♡ (inactif) / ♥ rouge (favori), toggle sans rechargement
  - Corps : Nom produit (fw-bold) + Description courte (text-muted, 2 lignes max)
  - Prix : salePrice barré (text-decoration: line-through, gris) + prix actuel (gras, couleur buttonColor)
  - Étoiles : ★★★★☆ (couleur #ffc107, rating/5)
  - Bouton "Ajouter au panier" (full-width, buttonColor/buttonTextColor)
  - Clic sur carte (hors bouton) → ouvre Quick-View modal

QUICK-VIEW MODAL (portal, overlay sombre) :
  - Colonne gauche : image grande + rangée miniatures cliquables (border 2px si sélectionnée)
  - Colonne droite : Nom, Badge, Prix, Description complète, Sélecteur quantité [−] [N] [+], Bouton "Ajouter"
  - Fermeture : clic backdrop ou bouton × en haut-droite

PANIER — SLIDE-IN DRAWER (toujours en DOM, position: fixed, right: 0, top: 0, height: 100vh) :
  - Ouvert/fermé via translateX(0) / translateX(100%)  — transition 0.3s ease
  - Backdrop semi-transparent derrière le drawer
  - En-tête : "Votre panier" + bouton × (ferme)
  - Corps : liste articles (image 60x60, nom, prix unitaire, sélecteur qté +/-, sous-total ligne)
  - Pied : Sous-total + Livraison + TVA + Total TTC — bouton "Commander" (ouvre checkout)
  - Panier vide : message + bouton "Continuer les achats"
  - Persistance : localStorage (connecté) ou sessionStorage (invité)

CHECKOUT — MODAL 3 ÉTAPES (React.createPortal) :
  - En-tête : indicateur d'étapes visuels [1 ●─ 2 ─ 3]
  - Étape 1 — Informations : prénom, nom, email, téléphone, adresse
  - Étape 2 — Livraison : Expédition standard OU Click & Collect (si activé); affiche adresse retrait + horaires
  - Étape 3 — Paiement : boutons radio [Stripe] [PayPal] [Espèces] selon paymentMethods config
      • Stripe → formulaire CardElement (stripe-js) ou lien de paiement
      • PayPal → bouton PayPal SDK
      • Espèces → confirmation simple
  - Navigation : [Retour] [Suivant/Confirmer]

TYPES DE DONNÉES CLÉS (à utiliser pour tout plugin similaire) :
  Product: { id, name, price, salePrice?, description, image, images?, category, stock, rating, tags?, badge?: 'new'|'sale'|'hot' }
  CartItem: { product: Product, quantity: number }
  paymentMethods: { stripe: boolean, paypal: boolean, cash: boolean }
  deliveryType: 'shipping' | 'click_and_collect' | 'both'
`,

    // Exemple complet pour guider la génération
    exampleConfig: {
      shopName: "Urban Style",
      shopDescription: "La mode urbaine à portée de main.",
      currency: "EUR",
      backgroundColor: "#f8f9fa",
      headerBackgroundColor: "#ffffff",
      productBackgroundColor: "#ffffff",
      buttonColor: "#000000",
      buttonTextColor: "#ffffff",
      products: [
        { name: "Sneakers Red", price: 89.99, description: "Confort absolu.", category: "Chaussures", stock: 10, image: "https://via.placeholder.com/300" },
        { name: "Hoodie Black", price: 45.00, description: "Coton premium.", category: "Vêtements", stock: 20, image: "https://via.placeholder.com/300" }
      ],
      paymentMethods: { stripe: true, paypal: true, cash: false },
      clickAndCollect: { enabled: true, address: "10 Rue de la Mode, Paris", instructions: "Retrait 10h-19h" },
      termsAndConditions: "<p>Conditions générales...</p>",
      termsRgpd: "<p>Politique RGPD...</p>"
    }
  } as any, // ✅ CORRECTION PRINCIPALE : Cast explicite pour autoriser 'capabilities'
  ConfigComponent: EcommerceConfigComponent,
  PreviewComponent: EcommerceShopRenderer,
  generateHTML: generateEcommerceHTML,
  defaultConfig: defaultConfig,
};

export default EcommerceShopPlugin;
